import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { withTranslation } from 'react-i18next';

class ErrorBoundaryBase extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      errorId: Date.now().toString()
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console and potentially to a logging service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log to external service if available
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: false,
        custom_map: {
          component: errorInfo.componentStack
        }
      });
    }
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, errorId } = this.state;
      const { t, i18n } = this.props;
      const isRTL = i18n.dir() === 'rtl';
      const {
        title = t('errorBoundary.title'),
        showDetails = false,
        showRetry = true,
        showReload = true,
        customMessage
      } = this.props;

      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full">
            {/* Error Icon and Title */}
            <div className="flex items-center justify-center mb-6">
              <div className="bg-red-100 rounded-full p-3 mr-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
                <p className="text-gray-600 mt-1">
                  {customMessage || t('errorBoundary.defaultMessage')}
                </p>
              </div>
            </div>

            {/* Error Details */}
            {showDetails && error && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">{t('errorBoundary.errorDetails')}</h3>
                <div className="text-sm text-gray-700 space-y-2">
                  <p><strong>{t('errorBoundary.error')}:</strong> {error.toString()}</p>
                  {errorId && <p><strong>{t('errorBoundary.errorId')}:</strong> {errorId}</p>}
                  {errorInfo && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
                        {t('errorBoundary.componentStack')}
                      </summary>
                      <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                        {errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            )}

            {/* Suggestions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">{t('errorBoundary.suggestions.title')}</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• {t('errorBoundary.suggestions.refresh')}</li>
                <li>• {t('errorBoundary.suggestions.checkInternet')}</li>
                <li>• {t('errorBoundary.suggestions.clearCache')}</li>
                <li>• {t('errorBoundary.suggestions.tryAgain')}</li>
                <li>• {t('errorBoundary.suggestions.contactSupport')}</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 justify-center">
              {showRetry && (
                <button
                  onClick={this.handleRetry}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t('errorBoundary.tryAgain')}
                </button>
              )}
              
              {showReload && (
                <button
                  onClick={this.handleReload}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Home className="w-4 h-4" />
                  {t('errorBoundary.reloadPage')}
                </button>
              )}
            </div>

            {/* Contact Information */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center text-sm text-gray-600">
              <p>
                {t('errorBoundary.contactSupport', { errorId: errorId || 'N/A' })}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Create the translated ErrorBoundary component
const ErrorBoundary = withTranslation()(ErrorBoundaryBase);

// Higher-order component for easier usage
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  return function WrappedComponent(props) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
};

// Hook for error handling in functional components
export const useErrorHandler = () => {
  const [error, setError] = React.useState(null);

  const handleError = React.useCallback((error, errorInfo = {}) => {
    console.error('Error caught by useErrorHandler:', error, errorInfo);
    
    // Log to external service
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: false
      });
    }

    setError(error);
  }, []);

  const clearError = React.useCallback(() => {
    setError(null);
  }, []);

  // Throw error to be caught by ErrorBoundary
  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return { handleError, clearError, error };
};

export default ErrorBoundary;